/**
 * Authentication Context
 * Manages JWT token received from the parent iframe via postMessage.
 * Provides authentication state to all components.
 *
 * Self-service mode (no parent iframe / no assignments pushed down): we
 * fetch authoritative lesson progress from the API. The assignments array
 * received from the parent is treated as a LATENCY HINT only — the source
 * of truth is always /api/lesson/progress/all.
 */

import { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import type { AssignedLesson, LessonStatus } from '../shared/types';
import { api } from '../services/api';
import { isAllowedOrigin, resetVerifiedParentOrigin, sendToParent } from '../lib/parentOrigins';
import { PERSONALITY_STORAGE_KEY } from './PersonalityContext.keys';
import { clearDarkModePreference } from '../hooks/useDarkMode';
import { logger } from '../lib/logger';

// Test score from parent dashboard
interface TestScore {
  score: number;
  completedAt: string;
}

interface AuthContextType {
  token: string | null;
  isAuthenticated: boolean;
  isAuthLoading: boolean; // True while waiting for auth data from parent iframe
  studentInfo: StudentInfo | null;
  assignedLessons: AssignedLesson[];
  testScores: Record<number, TestScore>; // Unit test scores { unitId: { score, completedAt } }
  simulationPhase: number; // 0 = not started, N = phase N completed
  setToken: (token: string) => void;
  setAssignedLessons: (lessons: AssignedLesson[]) => void;
  clearAuth: () => void;
}

interface StudentInfo {
  studentId: string;
  userId: string;
  schoolId: string;
  assignmentId: string;
  email: string;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setTokenState] = useState<string | null>(null);
  const [studentInfo, setStudentInfo] = useState<StudentInfo | null>(null);
  const [assignedLessons, setAssignedLessonsState] = useState<AssignedLesson[]>([]);
  const [testScores, setTestScores] = useState<Record<number, TestScore>>({});
  const [simulationPhase, setSimulationPhase] = useState<number>(0);
  const lastReloadTokenRef = useRef<string | null>(null);

  // Track if we're in an iframe and waiting for auth data
  const isInIframe = window.parent !== window;
  const [isAuthLoading, setIsAuthLoading] = useState(isInIframe); // Only loading if in iframe

  useEffect(() => {
    // Listen for messages from parent window (admin dashboard)
    const handleMessage = (event: MessageEvent) => {
      // Security: Only accept messages from exact whitelisted origins
      if (!isAllowedOrigin(event.origin)) {
        return;
      }

      // Handle authentication token
      if (event.data.type === 'AUTH_TOKEN') {
        const receivedToken = event.data.token;

        if (receivedToken) {
          setToken(receivedToken);
          // Don't set isAuthLoading = false here - wait for ASSIGNMENT_DATA
          // The access check needs assignment data, not just the token
        }
      }

      // Handle assignment data — treat as a latency hint; the API is the
      // authoritative source and we reload it on mount + token change.
      if (event.data.type === 'ASSIGNMENT_DATA') {
        const assignments = event.data.assignments as AssignedLesson[];
        const scores      = event.data.testScores as Record<number, TestScore> | undefined;
        const simPhase    = typeof event.data.simulationPhase === 'number' ? event.data.simulationPhase : 0;

        if (Array.isArray(assignments)) {
          if (assignments.length > 0) {
            setAssignedLessonsState(assignments);
            setIsAuthLoading(false);
          }

          setSimulationPhase(simPhase);

          if (scores && Object.keys(scores).length > 0) {
            setTestScores(scores);
          }
        }
      }
    };

    window.addEventListener('message', handleMessage);

    // Request token and assignment data from parent on mount (in case we missed initial message)
    if (window.parent !== window) {
      sendToParent({ type: 'REQUEST_AUTH_TOKEN' });
      sendToParent({ type: 'REQUEST_ASSIGNMENT_DATA' });
    }

    // Timeout for auth loading - if no response after 3 seconds, stop waiting
    const timeoutId = setTimeout(() => {
      setIsAuthLoading(false);
    }, 3000);

    return () => {
      window.removeEventListener('message', handleMessage);
      clearTimeout(timeoutId);
    };
  }, []);

  // Always reload authoritative progress from the API on mount + whenever the
  // token changes. Parent-provided assignments are a latency hint only.
  useEffect(() => {
    if (!token) return;
    if (lastReloadTokenRef.current === token) return;
    lastReloadTokenRef.current = token;

    api.setToken(token);

    const loadProgressFromAPI = async () => {
      try {
        const response = await api.getAllProgress();

        if (response.lessons.length > 0) {
          const lessonsFromAPI: AssignedLesson[] = response.lessons.map(lesson => ({
            lessonId: lesson.lessonId,
            assignmentId: lesson.progressId,
            status: lesson.status as LessonStatus,
            dueDate: undefined,
            score: lesson.score ?? undefined,
            completedAt: lesson.completedAt ?? undefined
          }));

          setAssignedLessonsState(lessonsFromAPI);
        }
      } catch (error) {
        logger.warn('Failed to load authoritative progress from API', { token_hash: hashToken(token) });
      } finally {
        setIsAuthLoading(false);
      }
    };

    // Small delay so a parent-pushed assignments payload (if any) gets a
    // chance to hydrate first; we then overwrite it with the API truth.
    const timeoutId = setTimeout(loadProgressFromAPI, 400);
    return () => clearTimeout(timeoutId);
  }, [token]);

  const setToken = (newToken: string) => {
    setTokenState(newToken);

    // Decode JWT to extract basic student info (signature validation happens
    // server-side). This is for display/UX only.
    try {
      const payload = JSON.parse(atob(newToken.split('.')[1]));
      setStudentInfo({
        studentId: payload.studentId,
        userId: payload.userId,
        schoolId: payload.schoolId,
        assignmentId: payload.assignmentId,
        email: payload.email
      });
    } catch (err) {
      logger.warn('Failed to decode JWT payload for display info');
    }
  };

  const clearAuth = () => {
    setTokenState(null);
    setStudentInfo(null);
    setAssignedLessonsState([]);
    setTestScores({});
    setSimulationPhase(0);
    lastReloadTokenRef.current = null;

    try { localStorage.removeItem(PERSONALITY_STORAGE_KEY); } catch { /* ignore */ }
    clearDarkModePreference();
    resetVerifiedParentOrigin();
  };

  const setAssignedLessons = (lessons: AssignedLesson[]) => {
    setAssignedLessonsState(lessons);
  };

  const value: AuthContextType = {
    token,
    isAuthenticated: !!token,
    isAuthLoading,
    studentInfo,
    assignedLessons,
    testScores,
    simulationPhase,
    setToken,
    setAssignedLessons,
    clearAuth
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

/**
 * Produce a short, non-reversible fingerprint of a JWT for logging.
 */
function hashToken(token: string): string {
  let h = 0;
  for (let i = 0; i < token.length; i++) h = ((h << 5) - h + token.charCodeAt(i)) | 0;
  return Math.abs(h).toString(36).slice(0, 8);
}
